/**
 * Client roster field normalization — shared by server API and client UI.
 * Keeps firstName, lastName, name, and customId consistent across create/update/read.
 */

import type { PainStatus } from '@/lib/types';

export interface ClientNameFields {
  firstName?: string;
  lastName?: string;
  name?: string;
}

export interface ClientRosterFields extends ClientNameFields {
  customId?: string;
}

type TimestampLike = {
  toDate?: () => Date;
  seconds?: number;
  _seconds?: number;
};

function trimOptionalString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Normalize a `needsTags` value (Phase 2B) into a deduped array of
 * `{ category, value }` taxonomy entries, dropping malformed/empty items.
 */
/** Abuse/size caps for needs tags: bounded count and per-field length. */
export const MAX_NEEDS_TAGS = 50;
export const MAX_NEEDS_TAG_FIELD_LEN = 120;

export function normalizeNeedsTags(value: unknown): Array<{ category: string; value: string }> {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const result: Array<{ category: string; value: string }> = [];
  for (const entry of value) {
    if (result.length >= MAX_NEEDS_TAGS) break;
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as Record<string, unknown>;
    const category = trimOptionalString(record.category);
    const tagValue = trimOptionalString(record.value);
    if (!category || !tagValue) continue;
    // Drop implausibly long entries rather than persist unbounded strings.
    if (category.length > MAX_NEEDS_TAG_FIELD_LEN || tagValue.length > MAX_NEEDS_TAG_FIELD_LEN) continue;
    const key = `${category}::${tagValue}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({ category, value: tagValue });
  }
  return result;
}

/** Split a legacy full name into first + last (first token / remainder). */
export function splitLegacyName(name: string): { firstName: string; lastName: string } {
  const trimmed = name.trim();
  if (!trimmed) {
    return { firstName: '', lastName: '' };
  }
  const spaceIndex = trimmed.indexOf(' ');
  if (spaceIndex === -1) {
    return { firstName: trimmed, lastName: '' };
  }
  return {
    firstName: trimmed.slice(0, spaceIndex).trim(),
    lastName: trimmed.slice(spaceIndex + 1).trim(),
  };
}

/** Build display name from parts; falls back to legacy name or empty string. */
export function buildDisplayName(fields: ClientNameFields): string {
  const first = trimOptionalString(fields.firstName);
  const last = trimOptionalString(fields.lastName);
  const combined = [first, last].filter(Boolean).join(' ').trim();
  if (combined) return combined;
  return trimOptionalString(fields.name) ?? '';
}

/**
 * Non-empty display label for any client doc — UI must never render a blank
 * name (legacy/preview docs can lack the computed `name` entirely, which made
 * roster rows appear "missing").
 */
export function clientDisplayLabel(fields: ClientRosterFields): string {
  return buildDisplayName(fields) || trimOptionalString(fields.customId) || 'Unnamed client';
}

/**
 * Fields that control document-level access.  These are managed exclusively
 * via the dedicated share / revoke endpoints and must never be overwritten by a
 * generic client update.  Stripping them here prevents a practitioner with
 * shared write access from locking the original owner out of their own record.
 */
const ACCESS_CONTROL_FIELDS = ['allowRead', 'allowWrite', '_createdBy'] as const;

// ─── Pain level → NRS 0–10 (Phase 6.3) ─────────────────────────────────────

/** Marker stamped on `Client.painScale` once `painLevel` is on the 0–10 NRS scale. */
export const PAIN_SCALE_NRS10 = 'nrs10' as const;

const LEGACY_PAIN_LEVEL_TO_NRS: Record<number, number> = { 1: 5, 2: 8 };

export interface PainLevelSource {
  painLevel?: number | null;
  painScale?: string | null;
}

/**
 * Normalize a client's `painLevel` to the clinical 0–10 Numeric Rating Scale
 * (NRS; 0 = pain-free) — the ONE shared read-side helper every consumer
 * (forms, drill-suggestion filters, Client 360 display) must go through.
 *
 * - `painScale === 'nrs10'` ⇒ already NRS; clamp defensively to [0, 10].
 * - Any other/absent `painScale` ⇒ legacy 0–2 (Minimal/Some/Lots) slider value;
 *   mapped via representative NRS midpoints: 1→5, 2→8. Legacy 0 ⇒ `undefined`
 *   (decision 2026-07-08): 0 was the old slider's untouched DEFAULT, so most
 *   stored 0s are "nobody assessed this", not a recorded "minimal pain" — a
 *   false "Not recorded" is clinically safer than a false "2/10 · Mild" claim
 *   plus a drill-scoring penalty nobody asked for. Out-of-range legacy values
 *   clamp to the nearest bucket rather than throw.
 * - `painLevel` absent/non-numeric ⇒ `undefined`. "Never recorded" is
 *   deliberately NOT the same claim as "pain-free" (NRS 0) — callers must not
 *   default this to 0; display sites should render "Not recorded" and scoring
 *   sites should treat `undefined` as no modifier.
 */
export function normalizePainLevelNrs(client: PainLevelSource | null | undefined): number | undefined {
  if (!client) return undefined;
  const { painLevel, painScale } = client;
  if (typeof painLevel !== 'number' || !Number.isFinite(painLevel)) return undefined;
  if (painScale === PAIN_SCALE_NRS10) {
    return Math.min(10, Math.max(0, Math.round(painLevel)));
  }
  const rounded = Math.round(painLevel);
  // Legacy 0 (and anything below) = the old slider's untouched default ⇒ not recorded.
  if (rounded <= 0) return undefined;
  if (rounded >= 2) return LEGACY_PAIN_LEVEL_TO_NRS[2];
  return LEGACY_PAIN_LEVEL_TO_NRS[1];
}

// ─── Pain status ⇄ level derivation (Mathias sign-off 2026-07-11) ───────────
// `painLevel` (0–10 NRS) is the LEADING, persisted value. `painStatus` is
// DERIVED from it — never stored, never trusted from a document. When an input
// carries only a status (no level), the level is derived from the status's
// representative NRS midpoint and THAT is what gets persisted. All of this
// lives here — inside the client API's shared normalization layer — so every
// consumer (API responses, drill planning, UI) sees one consistent pair.
// Rule: read pain ONLY via the client API / `deriveClientPain`, never raw from
// the database (see .cursor/rules/pain-data-via-client-api.mdc).

// Status vocabulary = Conor's methodology tiers (program templates are built
// on them — PR #7541 discussion): No Pain / Minimal / Some / Lots, mapping
// one-to-one onto the NRS buckets 0 / 1–3 / 4–6 / 7–10. This makes the legacy
// 0–2 slider round-trip exact: legacy 1 "Some Pain" → NRS 5 → 'some';
// legacy 2 "Lots of Pain" → NRS 8 → 'lots'.
export const PAIN_STATUSES: readonly PainStatus[] = ['no-pain', 'minimal', 'some', 'lots'];
export type { PainStatus };

export const PAIN_STATUS_LABEL: Record<PainStatus, string> = {
  'no-pain': 'No Pain',
  minimal: 'Minimal Pain',
  some: 'Some Pain',
  lots: 'Lots of Pain',
};

/**
 * Representative NRS midpoint per status — the level derived when an input
 * carries ONLY a status. 'some' (5) and 'lots' (8) intentionally coincide
 * with the legacy 0–2 tier mapping (1→5, 2→8), so a status-only write and a
 * legacy-tier migration of the same clinical claim land on the same number.
 */
export const PAIN_STATUS_TO_NRS: Record<PainStatus, number> = {
  'no-pain': 0,
  minimal: 2,
  some: 5,
  lots: 8,
};

/** Level → status derivation: 0 no-pain / 1–3 minimal / 4–6 some / 7–10 lots. */
export function derivePainStatus(nrs: number | undefined): PainStatus | undefined {
  if (nrs == null || !Number.isFinite(nrs)) return undefined;
  if (nrs <= 0) return 'no-pain';
  if (nrs <= 3) return 'minimal';
  if (nrs <= 6) return 'some';
  return 'lots';
}

/** Status → level derivation for status-only inputs. Non-whitelisted input ⇒ `undefined`. */
export function derivePainLevelFromStatus(status: unknown): number | undefined {
  if (typeof status !== 'string') return undefined;
  return (PAIN_STATUSES as readonly string[]).includes(status)
    ? PAIN_STATUS_TO_NRS[status as PainStatus]
    : undefined;
}

export interface DerivedClientPain {
  /** 0–10 NRS. Absent ⇒ never recorded (NOT the same claim as 0 = pain-free). */
  painLevel?: number;
  /** Always derived from `painLevel` (or, for status-only inputs, echoed after deriving the level). */
  painStatus?: PainStatus;
}

/**
 * THE single pain derivation — every read of client pain data goes through
 * this (via the client API's `normalizeClientForApi`, or directly for docs
 * that never crossed the API). Precedence:
 *
 * 1. A recorded `painLevel` (NRS or legacy 0–2, via {@link normalizePainLevelNrs})
 *    is the leading value; `painStatus` is derived from it. A stored/stray
 *    `painStatus` field is IGNORED in that case — status never overrides level.
 * 2. Only a valid `painStatus` given ⇒ derive the level from its midpoint.
 * 3. Neither ⇒ both absent ("never recorded").
 */
export function deriveClientPain(
  fields: (PainLevelSource & { painStatus?: unknown }) | null | undefined
): DerivedClientPain {
  const nrs = normalizePainLevelNrs(fields);
  if (nrs !== undefined) {
    return { painLevel: nrs, painStatus: derivePainStatus(nrs) };
  }
  const fromStatus = derivePainLevelFromStatus(fields?.painStatus);
  if (fromStatus !== undefined) {
    return { painLevel: fromStatus, painStatus: derivePainStatus(fromStatus) };
  }
  return {};
}

/**
 * On-demand migration (Mathias 2026-07-11): fields to persist when a client
 * doc still carries a legacy (pre-NRS) RECORDED pain value — apply on ANY save
 * of the client, even one that doesn't touch pain, so the stored value
 * converges to the 0–10 scale over time. Returns `undefined` when there is
 * nothing to migrate: already NRS-stamped, never recorded, or legacy 0
 * (= the old slider's untouched default ⇒ "not recorded" — there is no NRS
 * value to store for it, and writing one would stamp a false clinical claim;
 * reads already treat it as absent via {@link normalizePainLevelNrs}).
 */
export function legacyPainMigrationFields(
  data: PainLevelSource | null | undefined
): { painLevel: number; painScale: typeof PAIN_SCALE_NRS10 } | undefined {
  if (!data || data.painScale === PAIN_SCALE_NRS10) return undefined;
  const nrs = normalizePainLevelNrs(data);
  if (nrs === undefined) return undefined;
  return { painLevel: nrs, painScale: PAIN_SCALE_NRS10 };
}

/** "N/10 · Status" display string; "Not recorded" when painLevel was never captured. */
export function painLevelDisplayLabel(client: PainLevelSource | null | undefined): string {
  const nrs = normalizePainLevelNrs(client);
  if (nrs == null) return 'Not recorded';
  const status = derivePainStatus(nrs);
  return status ? `${nrs}/10 · ${PAIN_STATUS_LABEL[status]}` : `${nrs}/10`;
}

/**
 * Normalize incoming create/update payload: sync name ↔ firstName/lastName, trim customId.
 * Access-control fields (allowRead / allowWrite / _createdBy) are always stripped so that
 * a PATCH request cannot overwrite document-level permissions.
 */
export function normalizeClientWritePayload(
  input: Record<string, unknown>
): Record<string, unknown> {
  const output: Record<string, unknown> = { ...input };

  // Strip access-control fields unconditionally — they are set exclusively by
  // injectAccessControl (on create) and the share/revoke endpoints (on update).
  for (const field of ACCESS_CONTROL_FIELDS) {
    delete output[field];
  }

  const firstNamePresent = 'firstName' in input;
  const lastNamePresent = 'lastName' in input;
  const firstName = trimOptionalString(input.firstName);
  const lastName = trimOptionalString(input.lastName);
  const legacyName = trimOptionalString(input.name);

  if (firstNamePresent || lastNamePresent) {
    if (firstName !== undefined) output.firstName = firstName;
    else if (firstNamePresent) delete output.firstName;
    if (lastName !== undefined) output.lastName = lastName;
    else if (lastNamePresent) delete output.lastName;
    // Only sync the display name when BOTH components are present in this payload.
    // For partial updates (only one component provided) we cannot reconstruct the
    // full name without reading the existing document, so leave `name` untouched.
    if (firstNamePresent && lastNamePresent) {
      const displayName = buildDisplayName({ firstName, lastName, name: legacyName });
      if (displayName) output.name = displayName;
    }
  } else if (legacyName) {
    output.name = legacyName;
    const { firstName: parsedFirst, lastName: parsedLast } = splitLegacyName(legacyName);
    if (parsedFirst) output.firstName = parsedFirst;
    if (parsedLast) output.lastName = parsedLast;
  }

  const customId = trimOptionalString(input.customId);
  if (customId !== undefined) {
    output.customId = customId;
  } else if ('customId' in input) {
    delete output.customId;
  }

  if (Array.isArray(input.needsTags)) {
    output.needsTags = normalizeNeedsTags(input.needsTags);
  } else if ('needsTags' in input) {
    delete output.needsTags;
  }

  // Phase 6.3 — pain level → NRS 0–10. Any write through this normalizer is,
  // by design, treated as an NRS-aware write: clamp to [0, 10] and stamp the
  // `painScale` marker so read sites never mis-map a fresh 0–10 value through
  // the legacy 0–2 table. (The one write path that skips this normalizer —
  // ClientEditPage's direct-Firestore update — stamps the marker itself.)
  //
  // `painStatus` is DERIVED, never persisted (level is the leading value —
  // Mathias 2026-07-11). A status-only payload (no `painLevel`) derives the
  // level from the status midpoint and stores THAT; a payload carrying both
  // ignores the status entirely. The `painStatus` key itself is always
  // stripped below so no document ever stores a status that could drift out
  // of sync with its level.
  const statusDerivedLevel =
    'painLevel' in input ? undefined : derivePainLevelFromStatus(input.painStatus);
  if ('painLevel' in input) {
    const raw = input.painLevel;
    const num = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
    if (Number.isFinite(num)) {
      output.painLevel = Math.min(10, Math.max(0, Math.round(num)));
      output.painScale = PAIN_SCALE_NRS10;
    } else {
      delete output.painLevel;
    }
  } else if (statusDerivedLevel !== undefined) {
    output.painLevel = statusDerivedLevel;
    output.painScale = PAIN_SCALE_NRS10;
  }
  delete output.painStatus;

  return output;
}

function serializeTimestamp(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string') return value;
  const ts = value as TimestampLike;
  if (typeof ts.toDate === 'function') {
    return ts.toDate().toISOString();
  }
  const seconds = ts.seconds ?? ts._seconds;
  if (typeof seconds === 'number') {
    return new Date(seconds * 1000).toISOString();
  }
  return undefined;
}

/**
 * Normalize client document for API responses: backfill name parts, expose ISO timestamps.
 */
export function normalizeClientForApi(
  id: string,
  data: Record<string, unknown>
): Record<string, unknown> {
  const firstName = trimOptionalString(data.firstName);
  const lastName = trimOptionalString(data.lastName);
  const legacyName = trimOptionalString(data.name);

  let resolvedFirst = firstName;
  let resolvedLast = lastName;
  let resolvedName = legacyName;

  if (!resolvedFirst && !resolvedLast && resolvedName) {
    const parsed = splitLegacyName(resolvedName);
    resolvedFirst = parsed.firstName || undefined;
    resolvedLast = parsed.lastName || undefined;
  }

  if (!resolvedName) {
    resolvedName = buildDisplayName({
      firstName: resolvedFirst,
      lastName: resolvedLast,
      name: legacyName,
    }) || undefined;
  }

  const createdAt = serializeTimestamp(data.createdAt);
  const updatedAt = serializeTimestamp(data.updatedAt);
  const dateOfBirth = serializeTimestamp(data.dateOfBirth);
  const needsTags = normalizeNeedsTags(data.needsTags);

  const result: Record<string, unknown> = {
    ...data,
    id,
    ...(resolvedName ? { name: resolvedName } : {}),
    ...(resolvedFirst ? { firstName: resolvedFirst } : {}),
    ...(resolvedLast ? { lastName: resolvedLast } : {}),
    ...(trimOptionalString(data.customId) ? { customId: trimOptionalString(data.customId) } : {}),
    ...(needsTags.length > 0 ? { needsTags } : {}),
    ...(createdAt ? { createdAt } : {}),
    ...(updatedAt ? { updatedAt } : {}),
    ...(dateOfBirth ? { dateOfBirth } : {}),
  };

  // Never leak the sharing/ownership graph (the UIDs a client is shared with) to the client.
  // Access is managed server-side; the SPA has no need for these fields.
  for (const field of ACCESS_CONTROL_FIELDS) {
    delete result[field];
  }

  // Pain derivation happens INSIDE the client API (Mathias 2026-07-11): every
  // response carries the already-normalized 0–10 `painLevel` as the leading
  // value plus the `painStatus` derived from it — consumers never see a raw
  // legacy 0–2 value and never re-derive the status themselves. "Never
  // recorded" (including legacy 0) omits all three keys; a stored/stray
  // `painStatus` field is never echoed (status is derived, not stored).
  const derivedPain = deriveClientPain(data as PainLevelSource & { painStatus?: unknown });
  if (derivedPain.painLevel !== undefined) {
    result.painLevel = derivedPain.painLevel;
    result.painScale = PAIN_SCALE_NRS10;
    result.painStatus = derivedPain.painStatus;
  } else {
    delete result.painLevel;
    delete result.painScale;
    delete result.painStatus;
  }

  return result;
}

/** Tokenize client fields for roster search (name, email, customId). */
export function clientSearchHaystack(data: Record<string, unknown>): string[] {
  const first = trimOptionalString(data.firstName)?.toLowerCase() ?? '';
  const last = trimOptionalString(data.lastName)?.toLowerCase() ?? '';
  const combined = [first, last].filter(Boolean).join(' ').trim();
  const name = trimOptionalString(data.name)?.toLowerCase() ?? '';
  const email = trimOptionalString(data.email)?.toLowerCase() ?? '';
  const customId = trimOptionalString(data.customId)?.toLowerCase() ?? '';
  return [first, last, combined, name, email, customId].filter(Boolean);
}

